import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useSelectedRestaurant } from '../../context/SelectedRestaurantContext';
import { reservationsService, type Reservation } from '../../services/reservations';
import { adminRestaurantsService } from '../../services/adminRestaurants';

export default function ReservationsPage() {
  const { user } = useAuth();
  const { selectedRestaurantId } = useSelectedRestaurant();
  const [items, setItems] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptReservations, setAcceptReservations] = useState<boolean | null>(null);
  const [reservationsInfoUrl, setReservationsInfoUrl] = useState<string>('');

  const load = async () => {
    if (!user?.uid || !selectedRestaurantId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await reservationsService.list(user.uid, selectedRestaurantId);
      setItems(res);
    } catch (e) {
      setError('Error cargando reservaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [user?.uid, selectedRestaurantId]);

  useEffect(() => {
    const loadRestaurant = async () => {
      if (!user?.uid || !selectedRestaurantId) return;
      try {
        const r: any = await adminRestaurantsService.getRestaurantById(user.uid, selectedRestaurantId);
        setAcceptReservations(!!r?.acceptReservations);
        setReservationsInfoUrl(r?.reservationsInfoUrl || '');
      } catch (_e) {
        setAcceptReservations(null);
      }
    };
    loadRestaurant();
  }, [user?.uid, selectedRestaurantId]);

  const handleUpdateStatus = async (id: string, status: Reservation['status']) => {
    if (!user?.uid || !selectedRestaurantId) return;
    await reservationsService.updateStatus(user.uid, selectedRestaurantId, id, status);
    await load();
  };

  return (
    <div className="min-h-screen bg-[#e9ecef]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow-lg border border-[#22B6B2]/10 p-6 relative">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-[#2C4358]">Reservaciones</h2>
            <button
              onClick={load}
              className="px-4 py-2 rounded-xl border-2 border-[#22B6B2]/30 text-[#2C4358] bg-white hover:border-[#22B6B2]"
            >
              ↻ Refrescar
            </button>
          </div>

          {acceptReservations === false && (
            <div className="mb-4 p-4 rounded-2xl border-2 border-amber-300 bg-amber-50 text-amber-800">
              <div className="font-bold mb-1">Reservaciones desactivadas</div>
              <div className="text-sm">Activa “Aceptar reservaciones” en Restaurantes para gestionar reservaciones. {reservationsInfoUrl ? (<a href={reservationsInfoUrl} target="_blank" rel="noreferrer" className="underline">Más info</a>) : null}</div>
              <div className="mt-3">
                <a href="/admin/restaurants" className="inline-block px-4 py-2 rounded-xl bg-[#22B6B2] text-white font-bold">Ir a Restaurantes</a>
              </div>
            </div>
          )}

          {loading && (
            <div className="text-center py-10">
              <div className="w-12 h-12 border-4 border-[#22B6B2]/20 border-t-[#22B6B2] rounded-full animate-spin mx-auto"></div>
              <div className="mt-3 text-[#2C4358] font-semibold">Cargando...</div>
            </div>
          )}

          {error && (
            <div className="text-center py-6 text-red-600 font-semibold">{error}</div>
          )}

          {!loading && !error && (
            <div className={`overflow-x-auto ${acceptReservations === false ? 'pointer-events-none opacity-50 select-none' : ''}`}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mesa</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Capacidad</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha/Hora</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Personas</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {items.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700 truncate max-w-[160px]">{r.id}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{(r as any).table_number || r.table_id}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{(r as any).table_capacity ?? '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{new Date(r.reservation_date).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{r.party_size}</td>
                      <td className="px-4 py-3 text-sm font-semibold">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          r.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                          r.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          r.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{r.status}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right space-x-2 whitespace-nowrap">
                        {r.status !== 'confirmed' && (
                          <button onClick={() => handleUpdateStatus(String(r.id), 'confirmed')} className="px-3 py-1 rounded-lg bg-green-600 text-white hover:bg-green-700">Confirmar</button>
                        )}
                        {r.status !== 'cancelled' && (
                          <button onClick={() => handleUpdateStatus(String(r.id), 'cancelled')} className="px-3 py-1 rounded-lg bg-red-600 text-white hover:bg-red-700">Cancelar</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-gray-500">No hay reservaciones</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


